import { useEffect, useRef } from 'react';

export function useRealtimeSync({
  isStudent, remote, currentUser, currentTab, isStudentLocked,
  setTeacherCursor, setWhiteboardContents, setWhiteboards, setStudentCursor,
  setEditorContents,
  publishCursor, publishStudentCursor, publishScroll,
  publishEditorContents, editorContents, publishStudentEditorContents,
  publishMediaState, publishStudentMediaState,
  handleTabChange,
}) {
  // Consume remote state in student view
  useEffect(() => {
    if (!isStudent) return;
    setTeacherCursor(remote.teacherCursor || null);
    // Apply incoming whiteboard patch from teacher
    if (remote.wbPatch && remote.wbTargetId != null) {
      const key = `whiteboard-${remote.wbTargetId}`;
      setWhiteboardContents(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...(Array.isArray(remote.wbPatch.shapes) ? { shapes: remote.wbPatch.shapes } : {}),
          ...(remote.wbPatch.cursorPosition ? { cursorPosition: remote.wbPatch.cursorPosition } : {}),
        },
      }));
      // Auto-register boards the teacher created dynamically during the lesson
      setWhiteboards(prev => {
        if (prev.some(wb => String(wb.id) === String(remote.wbTargetId))) return prev;
        const wbType = remote.wbPatch.wbType || 'whiteboard';
        return [...prev, { id: remote.wbTargetId, type: wbType }];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote, isStudent]);

  // Teacher receives student cursor
  useEffect(() => {
    if (isStudent) return;
    setStudentCursor(remote.studentCursor || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.studentCursor, isStudent]);

  // Publish teacher cursor in real time
  useEffect(() => {
    if (isStudent) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleMove = (e) => {
      publishCursor && publishCursor({ x: e.clientX, y: e.clientY, name: currentUser?.name || 'Teacher' });
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, [isStudent, publishCursor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Publish student cursor in real time
  useEffect(() => {
    if (!isStudent) return;
    const handleMove = (e) => {
      publishStudentCursor && publishStudentCursor({ x: e.clientX, y: e.clientY, name: currentUser?.name || 'Student' });
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, [isStudent, publishStudentCursor]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Scroll sync (teacher → student) ---
  // Teacher: publish a 0..1 ratio so it survives viewport-height differences
  useEffect(() => {
    if (isStudent) return;
    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      const ratio = Math.min(1, Math.max(0, window.scrollY / max));
      publishScroll && publishScroll(ratio);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isStudent, publishScroll]);

  // Student in follow mode: apply teacher's scroll position
  useEffect(() => {
    if (!isStudentLocked) return;
    if (typeof remote?.scrollRatio !== 'number') return;
    const doc = document.documentElement;
    const max = Math.max(0, doc.scrollHeight - window.innerHeight);
    const target = remote.scrollRatio * max;
    // Avoid feedback loops: only scroll if we're far enough off
    if (Math.abs(window.scrollY - target) > 8) {
      window.scrollTo({ top: target, behavior: 'auto' });
    }
  }, [isStudentLocked, remote?.scrollRatio]);

  // --- Editor content sync (teacher → student during live session) ---
  // Teacher: publish snapshot of editorContents on every change (debounced in hook)
  useEffect(() => {
    if (isStudent) return;
    if (!publishEditorContents) return;
    publishEditorContents(editorContents);
  }, [isStudent, publishEditorContents, editorContents]);

  // Student: apply teacher's editorContents whenever they change.
  const lastAppliedEditorSeqRef = useRef(0);
  useEffect(() => {
    if (!isStudent) return;
    if (!remote?.editorContents) return;
    if ((remote.editorContentsSeq || 0) <= lastAppliedEditorSeqRef.current) return;
    lastAppliedEditorSeqRef.current = remote.editorContentsSeq || 0;
    // remote.editorContents is the TEACHER's own full editorContents
    // snapshot (published above for Follow Mode mirroring of the main
    // lesson containers) — it almost never has THIS student's
    // "homework-*" keys, since that's the teacher's own browser cache, not
    // this student's. A full setEditorContents(remote.editorContents)
    // replace wiped the student's entire homework content (including
    // anything just typed or submitted) the instant any Follow Mode
    // publish arrived, completely unrelated to their homework — this is
    // why teacher edits "saved" (this effect is gated on isStudent and
    // never runs for them) while the student's never did. Only adopt the
    // teacher's own lesson-container keys; never touch homework keys,
    // which are this student's private data.
    setEditorContents(prev => {
      const merged = { ...prev };
      Object.keys(remote.editorContents).forEach(key => {
        if (!key.startsWith('homework-')) {
          merged[key] = remote.editorContents[key];
        }
      });
      return merged;
    });
  }, [isStudent, remote?.editorContents, remote?.editorContentsSeq, setEditorContents]);

  // Student: publish live typing to teacher
  useEffect(() => {
    if (!isStudent) return;
    if (!publishStudentEditorContents) return;
    publishStudentEditorContents(editorContents);
  }, [isStudent, publishStudentEditorContents, editorContents]);

  // Teacher-side consumption of remote.studentEditorContents lives directly
  // in each <LessonContainer>'s liveContent prop below (gated on
  // !isFollowMode), not here — see the container render sites.

  // --- Media playback sync (doc/text editor mode) ---
  // Both sides: listen for window events fired by media node views (audio/
  // video/YouTube play, pause, seek) and publish to realtime — same "both
  // sides can act and see each other" model as the whiteboard, rather than
  // teacher-only.
  useEffect(() => {
    const handler = (e) => {
      const { src, action, currentTime } = e.detail || {};
      if (!src || !action) return;
      if (isStudent) publishStudentMediaState(src, action, currentTime || 0);
      else publishMediaState(src, action, currentTime || 0);
    };
    window.addEventListener('docMediaPublish', handler);
    return () => window.removeEventListener('docMediaPublish', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, publishMediaState, publishStudentMediaState]);

  // Student: when the teacher's mediaState changes, dispatch to media node views
  const lastAppliedMediaSeqRef = useRef(0);
  useEffect(() => {
    if (!isStudent) return;
    if (!remote?.mediaState) return;
    if ((remote.mediaStateSeq || 0) <= lastAppliedMediaSeqRef.current) return;
    lastAppliedMediaSeqRef.current = remote.mediaStateSeq || 0;
    window.dispatchEvent(new CustomEvent('docMediaSync', { detail: remote.mediaState }));
  }, [isStudent, remote?.mediaState, remote?.mediaStateSeq]);

  // Teacher: when the student's mediaState changes, dispatch to media node
  // views too — the reverse direction, so the teacher hears/sees whatever
  // the student plays.
  const lastAppliedStudentMediaSeqRef = useRef(0);
  useEffect(() => {
    if (isStudent) return;
    if (!remote?.studentMediaState) return;
    if ((remote.studentMediaStateSeq || 0) <= lastAppliedStudentMediaSeqRef.current) return;
    lastAppliedStudentMediaSeqRef.current = remote.studentMediaStateSeq || 0;
    window.dispatchEvent(new CustomEvent('docMediaSync', { detail: remote.studentMediaState }));
  }, [isStudent, remote?.studentMediaState, remote?.studentMediaStateSeq]);

  // Note: YouTube embeds in the editor (ResizableYoutube.jsx) now bridge
  // into docMediaPublish/docMediaSync themselves, via a real YT.Player
  // instance — same mechanism as regular audio/video. An earlier version of
  // this file tried to do that generically here via raw postMessage
  // sniffing, but YouTube only ever talks to a caller that completed the
  // YT.Player handshake, so it never actually received onStateChange.

  // --- Tab sync (teacher → student) ---
  useEffect(() => {
    if (!isStudent) return;
    if (typeof remote?.currentTab !== 'number') return;
    if (remote.currentTab === currentTab) return;
    handleTabChange(remote.currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, remote?.currentTab]);
}
